import Icon from 'app/components/partials/icon';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { useIdPolyfill } from '../../hooks/use-id-polyfill';
import React, { useRef, useState } from 'react';

const ArrowButton = ({ className, onClick, type, blockId }) => {
    const label = type === 'next' ? 'Next image' : 'Previous image';
    const arrowClickHandler = (event) => {
        event.stopPropagation();
        onClick();
    };

    return (
        <button
            className={className}
            onClick={arrowClickHandler}
            aria-label={label}
            aria-controls={blockId}
        >
            <Icon name="chevron" title={label} />
        </button>
    );
};

ArrowButton.propTypes = {
    className: PropTypes.string,
    type: PropTypes.string,
    onClick: PropTypes.func,
    blockId: PropTypes.string
};

const ImageGallery = ({ data, pageTitle }) => {
    const blockId = useIdPolyfill();
    const galleryRef = useRef();
    const [isLightboxActive, setIsLightboxActive] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    const toggleLightbox = () => {
        if (isDragging) {
            return;
        }
        setIsLightboxActive(!isLightboxActive);
    };

    const beforeSlideChangeHandler = (current, nextIndex) => {
        setActiveIndex(nextIndex);
        setIsDragging(true);
    };

    const renderImageWrapper = (item, index) => (
        // don't hide this, important for screen readers
        <div
            className="image-wrapper"
            key={index}
            onClick={toggleLightbox}
            aria-roledescription="slide"
        >
            <img src={item.file} alt={item.file_alt || pageTitle} />
            {isLightboxActive && <figcaption className="creditline || rich-editor-content" dangerouslySetInnerHTML={{ __html: item.creditline }}></figcaption>}
        </div>
    );

    // not important for lightbox to support accessibility features, as it's just an extra UX for sighted users
    const renderLightbox = () => {
        const lightboxSetting = {
            ...settings,
            variableWidth: false,
            initialSlide: activeIndex,
            adaptiveHeight: true,
            draggable: false,
            beforeChange: (current, next) => galleryRef.current.slickGoTo(next)
        };

        return (
            <div className="lightbox" onClick={toggleLightbox} aria-hidden="true">
                <Slider {...lightboxSetting}>
                    {data.map((item, index) => renderImageWrapper(item, index))}
                </Slider>
            </div>
        );
    };

    const settings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        variableWidth: true,
        adaptiveHeight: true,
        nextArrow: <ArrowButton type="next" blockId={blockId} />,
        prevArrow: <ArrowButton type="prev" blockId={blockId} />,
        beforeChange: beforeSlideChangeHandler,
        afterChange: () => setIsDragging(false),
    };

    return (
        <section className="content-block image-gallery-block || constrain-width large"
            aria-label="Image gallery"
            aria-roledescription="carousel"
        >
            <span role="status" aria-live="polite" className="sr-only">
                Now showing image {`${activeIndex + 1} of ${data.length}`}
            </span>
            <Slider {...settings} ref={galleryRef}>
                {data.map((item, index) => renderImageWrapper(item, index))}
            </Slider>
            {isLightboxActive && renderLightbox()}
        </section>
    );
};

ImageGallery.propTypes = {
    data: PropTypes.array.isRequired,
    pageTitle: PropTypes.string.isRequired
};

export default ImageGallery;
